import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TeamService, Team } from '../services/team.service';
import { WorkspaceData, ProfileData } from '../types/auth.types';
import { AuthService } from '../services/auth.service';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { 
  setWorkspaces, 
  setActiveWorkspace, 
  setLoading as setReduxLoading, 
  setError as setReduxError 
} from '../store/slices/workspaceSlice';
import { setAuthActiveWorkspace } from '../store/slices/authSlice';
import { setCredentials } from '../store/slices/authSlice';
import { store } from '../store';

export interface WorkspaceContextType {
  workspaces: WorkspaceData[];
  activeWorkspace: WorkspaceData | null;
  userProfiles: ProfileData[];
  loading: boolean;
  error: string | null;
  fetchWorkspaces: () => Promise<void>;
  setActiveWorkspace: (workspaceId: string) => Promise<boolean>;
  createWorkspace: (name: string, description: string) => Promise<boolean>;
  leaveWorkspace: (workspaceId: string) => Promise<boolean>;
  deleteWorkspace: (workspaceId: string) => Promise<boolean>;
  inviteToWorkspace: (workspaceId: string, email: string, roleId: number) => Promise<boolean>;
  switchUserProfile: (profileId: string) => Promise<boolean>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const reduxWorkspaces = useSelector((state: RootState) => state.workspace.workspaces);
  const reduxActiveWorkspace = useSelector((state: RootState) => state.workspace.activeWorkspace);
  const reduxLoading = useSelector((state: RootState) => state.workspace.loading);
  const reduxError = useSelector((state: RootState) => state.workspace.error);
  
  // Local state for profiles which isn't stored in Redux
  const [userProfiles, setUserProfiles] = useState<ProfileData[]>([]);
  const { user, isAuthenticated } = useAuth();

  const fetchWorkspaces = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      dispatch(setReduxLoading(true));
      dispatch(setReduxError(null));
      
      const profiles = await AuthService.getUserProfiles();
      setUserProfiles(profiles);
      console.log('profiles', profiles);
      
      const currentUserProfile = profiles.find(profile => profile.is_current_user);
      
      if (currentUserProfile && currentUserProfile.workspaces) {
        dispatch(setWorkspaces(currentUserProfile.workspaces));
        
        // The active workspace will be set by the setWorkspaces reducer
      }
    } catch (err: any) {
      console.error('Error fetching workspaces:', err);
      dispatch(setReduxError('Failed to load workspaces'));
      // toast.error('Failed to load workspaces');
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const setActiveWorkspaceById = async (workspaceId: string): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      const result = await AuthService.setActiveWorkspace(workspaceId);
      
      if (result.success) {
        // Find the workspace and set it as active in Redux
        const workspace = reduxWorkspaces.find(ws => ws.id === workspaceId);
        if (workspace) {
          // Create a workspace object with is_active set to true
          const updatedWorkspace: WorkspaceData = {
            ...workspace,
            is_active: true
          };

          // Update both Redux slices
          dispatch(setActiveWorkspace(updatedWorkspace));
          dispatch(setAuthActiveWorkspace(updatedWorkspace));

          // Update auth slice with new role and permissions from the response
          dispatch(setCredentials({
            ...store.getState().auth,
            role_id: result.role_id,
            permissions: result.permissions,
            team_id: workspaceId
          }));
        }
        
        // Refresh workspaces to get updated active status from the server
        await fetchWorkspaces();
        
        return true;
      } else {
        toast.error(result.message || 'Failed to switch workspace');
        return false;
      }
    } catch (err: any) {
      console.error('Error setting active workspace:', err);
      toast.error(err.message || 'Error switching workspace');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const switchUserProfile = async (profileId: string): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      const result = await AuthService.switchProfile(profileId);
      
      if (result.success) {
        // Refresh workspaces after switching profiles
        await fetchWorkspaces();
        return true;
      } else {
        // Use a default message if result.data?.message is not available
        const errorMessage = result.data?.message || 'Failed to switch user profile';
        toast.error(errorMessage);
        return false;
      }
    } catch (err: any) {
      console.error('Error switching user profile:', err);
      toast.error(err.message || 'Error switching user profile');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const createWorkspace = async (name: string, description: string): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      
      // Generate encrypted team key
      // In a real implementation, this would use proper encryption with the user's key
      const encrypted_team_key = "dummy_encrypted_key"; // Replace with actual encryption
      
      const result = await TeamService.createTeam(name, description, encrypted_team_key);
      
      if (result.success) {
        toast.success('Workspace created successfully');
        await fetchWorkspaces();
        return true;
      } else {
        toast.error(result.message || 'Failed to create workspace');
        return false;
      }
    } catch (err: any) {
      console.error('Error creating workspace:', err);
      toast.error(err.message || 'Error creating workspace');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const leaveWorkspace = async (workspaceId: string): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      
      // Use removeMemberFromTeam instead of leaveTeam
      await TeamService.removeMemberFromTeam(workspaceId);
      toast.success('Left workspace successfully');
      await fetchWorkspaces();
      return true;
    } catch (err: any) {
      console.error('Error leaving workspace:', err);
      toast.error(err.message || 'Error leaving workspace');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const deleteWorkspace = async (workspaceId: string): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      
      const result = await TeamService.deleteTeam(workspaceId);
      
      if (result.success) {
        toast.success('Workspace deleted successfully');
        await fetchWorkspaces();
        return true;
      } else {
        toast.error(result.message || 'Failed to delete workspace');
        return false;
      }
    } catch (err: any) {
      console.error('Error deleting workspace:', err);
      toast.error(err.message || 'Error deleting workspace');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  const inviteToWorkspace = async (workspaceId: string, email: string, roleId: number): Promise<boolean> => {
    try {
      dispatch(setReduxLoading(true));
      
      const result = await TeamService.inviteMember(workspaceId, email, roleId);
      
      if (result.success) {
        toast.success('Invitation sent successfully');
        return true;
      } else {
        toast.error(result.message || 'Failed to send invitation');
        return false;
      }
    } catch (err: any) {
      console.error('Error inviting to workspace:', err);
      toast.error(err.message || 'Error sending invitation');
      return false;
    } finally {
      dispatch(setReduxLoading(false));
    }
  };

  // Load workspaces on initial mount and when user changes
  useEffect(() => {
    console.log('WorkspaceContext useEffect triggered:', { isAuthenticated, user: !!user });
    if (isAuthenticated && user) {
      console.log('Calling fetchWorkspaces...');
      fetchWorkspaces();
    }
  }, [isAuthenticated, user?.id]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces: Array.isArray(reduxWorkspaces) ? reduxWorkspaces : [],
        activeWorkspace: reduxActiveWorkspace,
        userProfiles,
        loading: reduxLoading,
        error: reduxError,
        fetchWorkspaces,
        setActiveWorkspace: setActiveWorkspaceById,
        createWorkspace,
        leaveWorkspace,
        deleteWorkspace,
        inviteToWorkspace,
        switchUserProfile,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};